import { useEffect, useRef } from 'react'
import { useEvent } from '../context'

// Module-level to track if it's the initial page navigation for the app
let isInitialNavigation = true

// Test-only: reset the module-level initial-navigation flag. Needed because tests
// can share a module instance (Vitest isolate:false), where a prior widget render
// would otherwise consume the one-time initial-load state.
export const __resetWidgetLoadTimerForTests = () => {
  isInitialNavigation = true
}

interface UseWidgetLoadTimerOptions {
  widgetName: string
  isLoaded?: boolean
}

export const useWidgetLoadTimer = ({ widgetName, isLoaded = true }: UseWidgetLoadTimerOptions) => {
  const { onEvent } = useEvent()
  const startTime = useRef<number | null>(null)
  const hasLoaded = useRef(false)
  const isInitialLoad = useRef<boolean>(false)

  if (startTime.current === null) {
    isInitialLoad.current = isInitialNavigation
    startTime.current = isInitialNavigation ? performance.timeOrigin : Date.now()
  }

  useEffect(() => {
    if (isLoaded && !hasLoaded.current && startTime.current !== null) {
      const loadTimeMs = Date.now() - startTime.current

      onEvent('WidgetLoad', {
        widgetName,
        loadTimeMs,
        isInitialLoad: isInitialLoad.current,
      })

      hasLoaded.current = true
      startTime.current = null

      // Mark initial navigation complete AFTER first widget reports
      // This ensures all widgets on initial page/dashboard are isInitialLoad
      if (isInitialLoad.current) {
        isInitialNavigation = false
      }
    }
  }, [isLoaded, widgetName, onEvent])
}
